// js/components/qrModal.js
// Floating QR code modal — opens from the customer nav FAB over any page.
// Generates the QR canvas once per session (per user) and caches the DOM.

import QRCode from "qrcode";
import { auth } from "../lib/firebase.js";
import { getUser } from "../lib/firestore.js";
import { refreshUI } from "../lib/ui.js";

let modalEl = null;
let cachedUid = null;

// ─── Build DOM (once) ───────────────────────────────────────────────────────

function buildModal() {
  if (modalEl) return;

  modalEl = document.createElement("div");
  modalEl.className = "qr-modal-overlay";
  modalEl.setAttribute("role", "dialog");
  modalEl.setAttribute("aria-modal", "true");
  modalEl.setAttribute("aria-label", "QR Code");
  modalEl.hidden = true;

  modalEl.innerHTML = `
    <div class="qr-modal-card">
      <button class="qr-modal-close" aria-label="Κλείσιμο">
        <i data-lucide="x" aria-hidden="true"></i>
      </button>
      <div class="qr-modal-code" id="qr-modal-canvas-wrap">
        <p class="qr-modal-loading">Δημιουργία…</p>
      </div>
      <div class="qr-modal-user">
        <p class="qr-modal-name" id="qr-modal-name">—</p>
        <p class="qr-modal-hint">Δείξτε το στο ταμείο για να κερδίσετε πόντους</p>
      </div>
    </div>
  `;

  // Close on backdrop click
  modalEl.addEventListener("click", (e) => {
    if (e.target === modalEl) closeQrModal();
  });

  // Close button
  modalEl
    .querySelector(".qr-modal-close")
    .addEventListener("click", closeQrModal);

  // Escape key
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && !modalEl.hidden) closeQrModal();
  });

  document.body.appendChild(modalEl);
  refreshUI();
}

// ─── Generate QR canvas ─────────────────────────────────────────────────────

async function generateQR(user) {
  const canvasWrap = document.getElementById("qr-modal-canvas-wrap");
  const nameEl = document.getElementById("qr-modal-name");

  if (!canvasWrap || !nameEl) return;
  canvasWrap.innerHTML = `<p class="qr-modal-loading">Δημιουργία…</p>`;

  try {
    const profile = await getUser(user.uid);
    const displayName = profile?.name || user.displayName || "";
    const displayEmail = profile?.email || user.email || "";

    nameEl.textContent = displayName || displayEmail;

    // Keep payload small: uid + name + email only.
    // Photo is fetched from Firestore on the owner side — encoding a full
    // Google photo URL here would double the QR density for no benefit.
    const qrContent = `rewardlix:${user.uid}:${encodeURIComponent(displayName)}:${encodeURIComponent(displayEmail)}`;
    const canvas = document.createElement("canvas");

    await QRCode.toCanvas(canvas, qrContent, {
      width: 220,
      margin: 1,
      color: {
        dark: "#000000", // always black modules — standard QR, reliably scannable
        light: "#ffffff", // always white background
      },
    });

    canvasWrap.innerHTML = "";
    canvasWrap.appendChild(canvas);
    cachedUid = user.uid;
  } catch (err) {
    console.error("[qrModal]", err);
    canvasWrap.innerHTML = `<p class="qr-modal-loading" style="color:var(--color-error)">Αποτυχία δημιουργίας QR</p>`;
  }
}

// ─── Public API ─────────────────────────────────────────────────────────────

export function openQrModal() {
  buildModal();

  const user = auth.currentUser;
  if (!user) return;

  modalEl.hidden = false;
  requestAnimationFrame(() =>
    modalEl.classList.add("qr-modal-overlay--visible"),
  );

  // Re-generate QR only if the user changed since last open
  if (cachedUid !== user.uid) {
    generateQR(user);
  }
}

export function closeQrModal() {
  if (!modalEl) return;
  modalEl.classList.remove("qr-modal-overlay--visible");
  const hide = () => {
    if (modalEl) modalEl.hidden = true;
  };
  modalEl.addEventListener("transitionend", hide, { once: true });
  // Fallback in case transitionend doesn't fire (e.g. reduced-motion)
  setTimeout(hide, 250);
}
